import { create } from "zustand";

const apiUrl = process.env.REACT_APP_API_URL;

// Initialize displayed notifications in localStorage if not present
if (!localStorage.getItem("displayedNotifications")) {
  localStorage.setItem(
    "displayedNotifications",
    JSON.stringify({
      notifications: [],
      messages: [],
    })
  );
}

const useSignalingStore = create((set, get) => ({
  signalingServer: null,
  signalingServer2: null,
  signalingServer3: null,
  setSignalingServer: (server) => set({ signalingServer: server }),
  setSignalingServer2: (server) => set({ signalingServer2: server }),
  setSignalingServer3: (server) => set({ signalingServer3: server }),

  // Initialize notifications state
  notifications: [],
  unreadNotifications: [],
  notificationCounts: {},
  messagenotification: [],

  // Track recently processed messages to prevent duplicates
  recentlyProcessedMessages: new Set(),

  // Track if initialization is in progress to prevent concurrent calls
  isInitializing: false,

  // Initialize function to be called when app starts
  initializeNotifications: async () => {
    const state = get();

    // Prevent concurrent initialization calls
    if (state.isInitializing) {
      console.log("Initialization already in progress, skipping...");
      return;
    }

    console.log("Initializing notifications...");
    set({ isInitializing: true });

    try {
      const accessToken = localStorage.getItem("accessToken");
      const userId = localStorage.getItem("userId"); // Get user ID from localStorage

      if (!accessToken || !userId) {
        console.error("No access token or userId found");
        return;
      }

      console.log("Fetching notifications for user:", userId);

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      };

      console.log("Making parallel API requests...");

      // Fetch both unread notifications and messages in parallel
      const [notificationsResponse, messagesResponse] = await Promise.all([
        fetch(`${apiUrl}messaging/api/unread-notifications/${userId}/`, {
          method: "GET",
          headers: headers,
          credentials: "include",
        }),
        fetch(
          `${apiUrl}messaging/api/unread-message-notifications/${userId}/`,
          {
            method: "GET",
            headers: headers,
            credentials: "include",
          }
        ),
      ]);

      console.log("API responses received:", {
        notificationsStatus: notificationsResponse.status,
        messagesStatus: messagesResponse.status,
      });

      if (!notificationsResponse.ok || !messagesResponse.ok) {
        throw new Error(
          `Network response error: ${notificationsResponse.status} or ${messagesResponse.status}`
        );
      }

      // Parse responses sequentially to avoid "body stream already read" error
      let unreadNotifs = [];
      let unreadMessages = [];

      try {
        unreadNotifs = await notificationsResponse.json();
      } catch (error) {
        console.error("Error parsing notifications response:", error);
        unreadNotifs = [];
      }

      try {
        unreadMessages = await messagesResponse.json();
      } catch (error) {
        console.error("Error parsing messages response:", error);
        unreadMessages = [];
      }
      // console.log('unreadMessages: ', unreadMessages);
      console.log("Received initial unread notifications:", unreadNotifs.length);
      console.log("Received initial unread messages:", unreadMessages.length);

      // Log detailed content of notifications
      console.log("📋 DETAILED NOTIFICATIONS CONTENT:");
      if (Array.isArray(unreadNotifs) && unreadNotifs.length > 0) {
        unreadNotifs.forEach((notif, index) => {
          console.log(`🔔 Notification #${index + 1}:`, {
            id: notif.id,
            content: notif.content,
            sender_id: notif.sender_id,
            sender_name: notif.sender_name,
            sender_full_name: notif.sender_full_name,
            sender_image: notif.sender_image ? 'Present' : 'Missing',
            sender_company: notif.sender_company,
            sender_company_logo: notif.sender_company_logo ? 'Present' : 'Missing',
            timestamp: notif.timestamp || notif.created_at,
            read: notif.read,
            link: notif.link,
            type: notif.type,
            allFields: Object.keys(notif)
          });
        });
      } else {
        console.log("📭 No notifications received");
      }

      // Log detailed content of message notifications
      console.log("💬 DETAILED MESSAGE NOTIFICATIONS CONTENT:");
      if (Array.isArray(unreadMessages) && unreadMessages.length > 0) {
        unreadMessages.forEach((msg, index) => {
          console.log(`💌 Message #${index + 1}:`, {
            id: msg.id,
            content: msg.content || msg.message,
            sender_id: msg.sender_id,
            sender_name: msg.sender_name,
            sender_full_name: msg.sender_full_name,
            sender_image: msg.sender_image ? 'Present' : 'Missing',
            sender_company: msg.sender_company,
            sender_company_logo: msg.sender_company_logo ? 'Present' : 'Missing',
            timestamp: msg.timestamp,
            read: msg.read,
            type: msg.type,
            allFields: Object.keys(msg)
          });
        });
      } else {
        console.log("📭 No message notifications received");
      }

      // Group notifications by sender and count them
      const notificationCountsByUser = {};
      if (Array.isArray(unreadNotifs)) {
        unreadNotifs.forEach((notif) => {
          const senderId = notif.sender_id;
          if (senderId) {
            notificationCountsByUser[senderId] =
              (notificationCountsByUser[senderId] || 0) + 1;
          }
        });
      }

      // Process unread messages
      const messageNotifications = [];
      if (Array.isArray(unreadMessages)) {
        unreadMessages.forEach((message) => {
          messageNotifications.push({
            id: message.id,
            sender_id: message.sender_id,
            sender_name: message.sender_name || "New message",
            sender_image: message.sender_image,
            content: message.content || message.message || "New message",
            timestamp: message.timestamp || new Date().toISOString(),
            read: false,
          });
        });
      }

      // Update store with initial data, but preserve any new real-time notifications
      set((state) => {
        // Get any existing notifications that are NOT silent (real-time notifications)
        const existingRealTimeNotifications =
          state.notifications.filter((notif) => !notif.silent) || [];

        console.log(
          "🔄 Preserving real-time notifications during initialization:",
          {
            existingRealTime: existingRealTimeNotifications.length,
            newFromAPI: unreadNotifs.length,
          }
        );

        // Combine real-time notifications with API notifications
        const apiNotifications = Array.isArray(unreadNotifs)
          ? unreadNotifs.map((notif) => ({
              ...notif,
              silent: true, // Mark loaded notifications as silent to prevent snackbar display
            }))
          : [];

        // Put real-time notifications first (they're newer)
        const combinedNotifications = [
          ...existingRealTimeNotifications,
          ...apiNotifications,
        ];
        const combinedUnreadNotifications = [
          ...existingRealTimeNotifications,
          ...apiNotifications,
        ];

        return {
          unreadNotifications: combinedUnreadNotifications,
          notifications: combinedNotifications,
          notificationCounts: notificationCountsByUser,
          messagenotification: messageNotifications.map((msg) => ({
            ...msg,
            silent: true, // Mark loaded messages as silent to prevent snackbar display
          })),
        };
      });

      console.log(
        "Notifications initialized with counts:",
        notificationCountsByUser
      );
      console.log(
        "Message notifications initialized:",
        messageNotifications.length
      );
    } catch (error) {
      console.error("Error initializing notifications:", error);

      // More detailed error logging
      if (error.message.includes("body stream already read")) {
        console.error(
          "Body stream error detected - this usually means a response was consumed twice"
        );
      }

      set({
        unreadNotifications: [],
        notifications: [],
        notificationCounts: {},
        messagenotification: [],
      });
    } finally {
      // Always reset the initialization flag
      set({ isInitializing: false });
    }
  },

  messages: [],
  addMessage: (message) => {
    console.log("Adding new message:", message);
    set((state) => {
      // Make sure message has a timestamp
      const messageWithTimestamp = {
        ...message,
        timestamp: message.timestamp || new Date().toISOString(),
      };

      // Add the message to the array
      const updatedMessages = [...state.messages, messageWithTimestamp];

      // Sort messages by timestamp in chronological order
      updatedMessages.sort((a, b) => {
        return new Date(a.timestamp) - new Date(b.timestamp);
      });

      return { messages: updatedMessages };
    });
  },

  setNotifications: (notifications) => {
    console.log("Setting all notifications:", notifications);
    set({ notifications: Array.isArray(notifications) ? notifications : [] });
  },

  setUnreadNotifications: (unreadNotifications) => {
    console.log("Setting unread notifications:", unreadNotifications);
    set({
      unreadNotifications: Array.isArray(unreadNotifications)
        ? unreadNotifications
        : [],
    });
  },

  addNotification: (notification) =>
    set((state) => {
      console.log("Current notifications state:", {
        all: state.notifications.length,
        unread: state.unreadNotifications.length,
      });

      // Enhanced logging of new notification content
      console.log("🆕 ADDING NEW NOTIFICATION:");
      console.log("📋 Full notification object:", notification);
      console.log("🔔 Notification details:", {
        id: notification.id,
        type: notification.type,
        content: notification.content,
        sender_id: notification.sender_id,
        sender_name: notification.sender_name,
        sender_full_name: notification.sender_full_name,
        sender_image: notification.sender_image ? 'Present' : 'Missing',
        sender_company: notification.sender_company,
        sender_company_logo: notification.sender_company_logo ? 'Present' : 'Missing',
        timestamp: notification.timestamp || notification.created_at || new Date().toISOString(),
        read: notification.read,
        silent: notification.silent,
        link: notification.link,
        allFields: Object.keys(notification)
      });

      const currentNotifications = Array.isArray(state.notifications)
        ? state.notifications
        : [];
      const currentUnreadNotifications = Array.isArray(
        state.unreadNotifications
      )
        ? state.unreadNotifications
        : [];

      return {
        notifications: [notification, ...currentNotifications],
        unreadNotifications: [notification, ...currentUnreadNotifications],
      };
    }),

  markNotificationAsRead: async (notificationId) => {
    console.log("Marking notification as read:", notificationId);
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      };

      console.log(
        `Making API call to mark notification ${notificationId} as read`
      );
      const response = await fetch(
        `${apiUrl}messaging/api/mark-notification-read/${notificationId}/`,
        {
          method: "POST",
          headers: headers,
          credentials: "include",
        }
      );

      console.log("API response status:", response.status);

      if (!response.ok) {
        throw new Error(`Network response error: ${response.status}`);
      }

      // Add notification ID to tracked displayed notifications
      try {
        // Load existing displayed notifications
        let displayedNotifications = JSON.parse(
          localStorage.getItem("displayedNotifications")
        ) || {
          notifications: [],
          messages: [],
        };

        // Add this notification ID if not already present
        if (!displayedNotifications.notifications.includes(notificationId)) {
          displayedNotifications.notifications.push(notificationId);
          localStorage.setItem(
            "displayedNotifications",
            JSON.stringify(displayedNotifications)
          );
        }

        // Update window variable if available
        if (window.displayedNotificationIds) {
          if (
            !window.displayedNotificationIds.notifications.includes(
              notificationId
            )
          ) {
            window.displayedNotificationIds.notifications.push(notificationId);
          }
        }
      } catch (error) {
        console.error("Error updating displayed notification tracking:", error);
      }

      set((state) => {
        const updatedState = {
          notifications: state.notifications.map((notif) =>
            notif.id === notificationId
              ? { ...notif, read: true, silent: true }
              : notif
          ),
          unreadNotifications: state.unreadNotifications.filter(
            (notif) => notif.id !== notificationId
          ),
        };
        console.log("Updated notification state after marking as read:", {
          all: updatedState.notifications.length,
          unread: updatedState.unreadNotifications.length,
        });
        return updatedState;
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  },

  fetchUnreadNotifications: async () => {
    console.log("Fetching unread notifications...");
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      };

      console.log("Making API call to fetch unread notifications");
      const response = await fetch(
        `${apiUrl}messaging/api/unread-notifications/`,
        {
          method: "GET",
          headers: headers,
          credentials: "include",
        }
      );

      console.log("Unread notifications response status:", response.status);

      if (!response.ok) {
        throw new Error(`Network response error: ${response.status}`);
      }

      const data = await response.json();
      console.log("Received unread notifications from API:", data);

      // Log each individual notification for debugging
      if (Array.isArray(data)) {
        data.forEach((notification, index) => {
          console.log(`Unread notification #${index + 1}:`, {
            id: notification.id,
            type: notification.type,
            content: notification.content,
            from:
              notification.sender_id || notification.sender_name || "Unknown",
            timestamp: notification.created_at || "No timestamp",
          });
        });
      }

      set((state) => {
        const newNotifications = Array.isArray(data) ? data : [];
        const currentNotifications = Array.isArray(state.notifications)
          ? state.notifications
          : [];

        const updatedState = {
          unreadNotifications: newNotifications,
          notifications: [
            ...newNotifications,
            ...currentNotifications.filter((n) => n.read),
          ],
        };

        console.log("Updated notification state after fetch:", {
          all: updatedState.notifications.length,
          unread: updatedState.unreadNotifications.length,
        });

        return updatedState;
      });
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
      set({ unreadNotifications: [], notifications: [] });
    }
  },

  markNotificationsAsRead: async () => {
    console.log("Marking all notifications as read");
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        console.error("No access token found");
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      };

      console.log("Making API call to mark all notifications as read");
      const response = await fetch(
        `${apiUrl}messaging/api/mark-notifications-read/`,
        {
          method: "POST",
          headers: headers,
          credentials: "include",
        }
      );

      console.log("API response status:", response.status);

      if (!response.ok) {
        throw new Error(`Network response error: ${response.status}`);
      }

      const updatedNotifications = await response.json();
      console.log(
        "Received updated notifications from API:",
        updatedNotifications
      );

      // Add all notification IDs to tracked displayed notifications
      try {
        const currentState = get();
        const notificationIds = currentState.notifications.map(
          (notif) => notif.id
        );

        // Load existing displayed notifications
        let displayedNotifications = JSON.parse(
          localStorage.getItem("displayedNotifications")
        ) || {
          notifications: [],
          messages: [],
        };

        // Add all notification IDs
        notificationIds.forEach((id) => {
          if (!displayedNotifications.notifications.includes(id)) {
            displayedNotifications.notifications.push(id);
          }
        });

        localStorage.setItem(
          "displayedNotifications",
          JSON.stringify(displayedNotifications)
        );

        // Update window variable if available
        if (window.displayedNotificationIds) {
          notificationIds.forEach((id) => {
            if (!window.displayedNotificationIds.notifications.includes(id)) {
              window.displayedNotificationIds.notifications.push(id);
            }
          });
        }
      } catch (error) {
        console.error("Error updating displayed notification tracking:", error);
      }

      set({
        notifications: Array.isArray(updatedNotifications)
          ? updatedNotifications.map((n) => ({
              ...n,
              read: true,
              silent: true,
            }))
          : [],
        unreadNotifications: [],
      });

      console.log("All notifications marked as read, unread count reset to 0");
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  },

  messageNotificationCounts: {},

  updateMessageNotificationCount: (senderId) => {
    if (!senderId) {
      console.warn(
        "updateMessageNotificationCount called with invalid senderId"
      );
      return;
    }

    set((state) => {
      const newCount = (state.messageNotificationCounts[senderId] || 0) + 1;
      console.log(
        `Updated message notification count for sender ${senderId}: ${newCount}`
      );
      return {
        messageNotificationCounts: {
          ...state.messageNotificationCounts,
          [senderId]: newCount,
        },
      };
    });
  },

  resetMessageNotificationCount: (senderId) => {
    console.log(`Resetting message notification count for sender ${senderId}`);
    set((state) => ({
      messageNotificationCounts: {
        ...state.messageNotificationCounts,
        [senderId]: 0,
      },
    }));
  },

  getTotalMessageNotifications: () => {
    const state = get();
    const total = Object.values(state.messageNotificationCounts).reduce(
      (a, b) => a + b,
      0
    );
    console.log("Total message notifications:", total);
    return total;
  },

  handleMessageNotification: (data) => {
    console.log("Handling message notification:", data);
    if (data.type === "notif_from_messages") {
      console.log("Received a message notification:", {
        type: data.type,
        notification: data.notif,
        senderId: data.sender_id,
      });

      // Check if this notification was already processed
      const state = get();
      const exists = state.messagenotification.some((n) => n.id === data.id);
      if (!exists) {
        get().updateMessageNotificationCount(data.sender_id);
      }
    }
  },

  updateNotificationCount: (userId, increment = 1) => {
    if (!userId) {
      console.warn("updateNotificationCount called with invalid userId");
      return;
    }

    console.log(
      `updateNotificationCount called for user ${userId} with increment ${increment}`
    );

    // This function should only be called for actual notifications, not messages
    // Messages should use updateMessageNotificationCount instead
    set((state) => {
      const newCount = (state.notificationCounts[userId] || 0) + increment;
      console.log(`Updated notification count for user ${userId}: ${newCount}`);
      return {
        notificationCounts: {
          ...state.notificationCounts,
          [userId]: newCount,
        },
      };
    });
  },

  resetNotificationCount: (userId) => {
    console.log(`Resetting notification count for user ${userId}`);
    set((state) => ({
      notificationCounts: {
        ...state.notificationCounts,
        [userId]: 0,
      },
    }));
  },

  getTotalNotifications: () => {
    const state = get();
    const total = Object.values(state.notificationCounts).reduce(
      (a, b) => a + b,
      0
    );
    console.log("Total notifications:", total);
    return total;
  },

  addMessageNotification: (notification) => {
    console.log("🆕 ADDING NEW MESSAGE NOTIFICATION:");
    console.log("💬 Full message notification object:", notification);
    console.log("💌 Message notification details:", {
      id: notification.id,
      content: notification.content || notification.message,
      sender_id: notification.sender_id,
      sender_name: notification.sender_name,
      sender_full_name: notification.sender_full_name || notification.senderFullName,
      sender_image: notification.sender_image || notification.senderImage ? 'Present' : 'Missing',
      sender_company: notification.sender_company || notification.senderCompany,
      sender_company_logo: notification.sender_company_logo || notification.senderCompanyLogo ? 'Present' : 'Missing',
      timestamp: notification.timestamp,
      read: notification.read,
      silent: notification.silent,
      type: notification.type,
      allFields: Object.keys(notification)
    });

    const state = get();

    // Create a unique key for this message to track duplicates
    const messageKey = `${notification.sender_id}_${notification.message}_${notification.timestamp}`;

    // Check if we've recently processed this message
    if (state.recentlyProcessedMessages.has(messageKey)) {
      console.log("Recently processed message detected, skipping:", messageKey);
      return;
    }

    // Check if this notification already exists to prevent duplicates
    const exists = state.messagenotification.some(
      (n) =>
        n.id === notification.id ||
        (n.sender_id === notification.sender_id &&
          n.message === notification.message &&
          Math.abs(new Date(n.timestamp) - new Date(notification.timestamp)) <
            2000) // Within 2 seconds
    );

    if (exists) {
      console.log(
        "Duplicate message notification detected, skipping:",
        notification.id
      );
      return; // Don't add duplicate
    }

    // Add to recently processed messages
    const newRecentlyProcessed = new Set(state.recentlyProcessedMessages);
    newRecentlyProcessed.add(messageKey);

    // Clean up old entries (keep only last 100)
    if (newRecentlyProcessed.size > 100) {
      const entries = Array.from(newRecentlyProcessed);
      const toKeep = entries.slice(-50); // Keep last 50
      newRecentlyProcessed.clear();
      toKeep.forEach((key) => newRecentlyProcessed.add(key));
    }

    set((state) => ({
      messagenotification: [...state.messagenotification, notification],
      recentlyProcessedMessages: newRecentlyProcessed,
    }));

    // Only update message notification count if it's a new message and not silent
    if (notification.sender_id && !notification.silent) {
      console.log(
        "Updating message notification count for sender:",
        notification.sender_id
      );
      get().updateMessageNotificationCount(notification.sender_id);
    }

    // Clean up the recently processed messages after 5 seconds
    setTimeout(() => {
      set((state) => {
        const updatedRecentlyProcessed = new Set(
          state.recentlyProcessedMessages
        );
        updatedRecentlyProcessed.delete(messageKey);
        return { recentlyProcessedMessages: updatedRecentlyProcessed };
      });
    }, 5000);
  },

  clearMessageNotifications: () => {
    console.log("Clearing all message notifications");

    // Track all message notifications as displayed in localStorage
    try {
      const state = get();
      const messageIds = state.messagenotification.map(
        (notification) => notification.id
      );

      // Load existing displayed notifications
      let displayedNotifications = JSON.parse(
        localStorage.getItem("displayedNotifications")
      ) || {
        notifications: [],
        messages: [],
      };

      // Add all message notification IDs
      messageIds.forEach((id) => {
        if (!displayedNotifications.messages.includes(id)) {
          displayedNotifications.messages.push(id);
        }
      });

      localStorage.setItem(
        "displayedNotifications",
        JSON.stringify(displayedNotifications)
      );

      // Update window variable if available
      if (window.displayedNotificationIds) {
        messageIds.forEach((id) => {
          if (!window.displayedNotificationIds.messages.includes(id)) {
            window.displayedNotificationIds.messages.push(id);
          }
        });
      }
    } catch (error) {
      console.error(
        "Error updating displayed message notification tracking:",
        error
      );
    }

    set({ messagenotification: [] });
  },

  removeMessageNotification: (notificationId) => {
    console.log(`Removing message notification with ID: ${notificationId}`);
    set((state) => ({
      messagenotification: state.messagenotification.filter(
        (n) => n.id !== notificationId
      ),
    }));
  },

  setInitialNotificationCounts: (counts) => {
    console.log("Setting initial notification counts:", counts);
    set((state) => ({
      notificationCounts: {
        ...state.notificationCounts,
        ...counts,
      },
    }));
  },

  setInitialMessageNotificationCounts: (counts) => {
    console.log("Setting initial message notification counts:", counts);
    set((state) => ({
      messageNotificationCounts: {
        ...state.messageNotificationCounts,
        ...counts,
      },
    }));
  },
}));

export default useSignalingStore;
